import type { ReactElement } from 'react';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { useSyncBlockStoreManagerForContent } from '@confluence/fabric-providers/entry-points/SyncedBlockProvider.ts';

import type { SyncBlockContentMap, SyncBlockReference } from './handleADF.ts';
import { SyncBlockTocSubscribers, type SyncBlockTocEntry } from './SyncBlockTocSubscribers.tsx';

export type UseSyncBlockTocHeadingsResult = {
	syncBlockContentMap: SyncBlockContentMap;
	// `undefined` when the feature is disabled, so the caller renders no
	// subscribers at all.
	subscribersElement?: ReactElement;
};

const EMPTY_SYNC_BLOCK_CONTENT_MAP: SyncBlockContentMap = new Map<string, ADFEntity[]>();

/**
 * Collects resolved synced-block content into a single `Map` keyed by each
 * reference node's `localId`. The actual fetch/subscribe is delegated to
 * `<SyncBlockTocSubscribers>` (returned as `subscribersElement`), which mounts
 * one render-nothing component per reference backed by `useFetchSyncBlockData`.
 * Those subscribers share the reference-block renderer's in-memory cache, so no
 * additional network requests are triggered for the TOC, and they receive live
 * updates when a source block changes.
 */
export const useSyncBlockTocHeadings = (
	references: SyncBlockReference[],
	enabled: boolean,
	contentId: string | undefined,
	contentType: string | undefined,
): UseSyncBlockTocHeadingsResult => {
	// Stable string derived from both parts of each reference identity, so the memo
	// below keeps a stable identity when `references` is recreated with the same
	// logical contents while still updating when a localId points at a new resource.
	const refKeys = useMemo(
		() =>
			JSON.stringify(
				references
					.map(({ localId, resourceId }) => [localId, resourceId])
					.sort(([localIdA, resourceIdA], [localIdB, resourceIdB]) =>
						localIdA === localIdB
							? resourceIdA.localeCompare(resourceIdB)
							: localIdA.localeCompare(localIdB),
					),
			),
		[references],
	);
	const uniqueReferences = useMemo(() => {
		const byLocalId = new Map<string, SyncBlockReference>();
		for (const reference of references) {
			byLocalId.set(reference.localId, reference);
		}
		return Array.from(byLocalId.values());
		// `references` is intentionally read inside the memo but excluded from the
		// deps; `refKeys` captures the only relevant change in its contents.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refKeys]);

	const [syncBlockContentMap, setSyncBlockContentMap] = useState<SyncBlockContentMap>(
		() => new Map<string, ADFEntity[]>(),
	);

	// Held as a ref so the onEntry callback identity is stable across renders.
	// Without this, every render would re-mount the subscribers and re-trigger
	// their effects.
	const mapRef = useRef(syncBlockContentMap);
	mapRef.current = syncBlockContentMap;

	const handleEntry = useCallback((entry: SyncBlockTocEntry) => {
		const existing = mapRef.current.get(entry.key);
		if (existing && areArraysShallowEqual(existing, entry.content)) {
			return;
		}
		setSyncBlockContentMap((current) => {
			const next = new Map(current);
			next.set(entry.key, entry.content);
			return next;
		});
	}, []);

	// Build a store manager scoped to this content. The manager shares the
	// renderer's in-memory cache, so subscribers reuse already-fetched data.
	// Hooks run unconditionally to keep hook order stable; the return branches on
	// `enabled` below.
	const manager = useSyncBlockStoreManagerForContent({ contentId, contentType });

	const subscribersElement = useMemo(
		() =>
			enabled ? (
				<SyncBlockTocSubscribers
					manager={manager}
					references={uniqueReferences}
					onEntry={handleEntry}
				/>
			) : undefined,
		[enabled, manager, uniqueReferences, handleEntry],
	);

	// When disabled, return inert values so the caller mounts no subscribers and
	// the TOC walker sees an empty map.
	if (!enabled) {
		return { syncBlockContentMap: EMPTY_SYNC_BLOCK_CONTENT_MAP };
	}

	return { syncBlockContentMap, subscribersElement };
};

const areArraysShallowEqual = <T,>(a: T[], b: T[]): boolean => {
	if (a === b) {
		return true;
	}
	if (a.length !== b.length) {
		return false;
	}
	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i]) {
			return false;
		}
	}
	return true;
};
